/**
 * Provider-supplied customer reference carried from an inbound link into the CRM.
 *
 * See AI-Analysis/reference-id-passthrough.md for the full flow.
 */

/** Canonical spelling. Use this when generating links. */
export const REFERENCE_PARAM = 'reference_id';

/**
 * Accepted inbound spellings, normalised to lowercase alphanumerics — see
 * normaliseParamKey() in lib/captureReference.ts.
 *
 * Two entries cover every requested variant, because normalising collapses case and
 * separators: 'referenceid' matches reference_id, ReferenceID, ReferenceId, Reference-Id,
 * REFERENCE_ID; 'refid' matches ref_id, RefID, RefId, ref-id.
 *
 * Providers build these links by hand, so being forgiving here costs nothing and saves a
 * support round-trip. Note lead-create also accepts ReferenceNum ('referencenum') — add it
 * if any provider turns out to send that spelling in a URL.
 */
export const REFERENCE_PARAM_ALIASES = new Set(['referenceid', 'refid']);

/** Suffixed with the partner slug at runtime — see referenceStorageKey() in referenceStore.ts. */
export const REFERENCE_STORAGE_PREFIX = 'osc.reference';

/**
 * Attribution window. Bounds how long a stored reference can sit before it mis-credits an
 * unrelated later order, which matters most on shared and family devices.
 */
export const REFERENCE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * A failing bind must not POST on every navigation for the rest of the session. A missing
 * reference is not a broken checkout.
 */
export const REFERENCE_MAX_BIND_ATTEMPTS = 3;

/**
 * Provisional. Customer.PartnerReferenceID is varchar(255); 64 is a deliberately tighter
 * bound until the providers' real id format is known.
 */
export const REFERENCE_PATTERN = /^[A-Za-z0-9._:-]{1,64}$/;
